import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";

// Components
import BaseModal from "~/components/modals/BaseModal";
import { useStore } from "~/hooks/useStore";
// Types
import RootStore from "~/store/RootStore";
// Styles
import s from "~/components/modals/appointment/AppointmentConfirmModal.module.scss";
import { formatDate } from "~/utils/helpers";
import { IAppointmentCreatePayload } from "~/types/appointment";

interface Props {
  isOpened: boolean;
  onClose: () => void;
  onConfirm: (payload: IAppointmentCreatePayload) => void;
}

const AppointmentConfirmModal: React.FC<Props> = observer(
  ({ isOpened = false, onClose, onConfirm }) => {
    const store: RootStore = useStore();
    const [open, setOpen] = useState<boolean>(isOpened);
    const patient = store.patientStore.currentPatient;
    const appointmentTicketTime = store.appointmentStore.currentTicketTimeSlot;
    const appointmentTicket = store.appointmentStore.currentTicket;
    const hospital = store.hospitalStore.currentHospital;
    const specialist = store.specialistStore.currentSpecialist;
    const speciality = store.specialityStore.currentSpeciality;
    const currentCity = store.cityStore.currentCity;
    const time = store.appointmentStore.chosenEgisFormattedDate;

    const weekDay = appointmentTicket
      ? formatDate(appointmentTicket.value, "ccc")
      : "";

    const day = appointmentTicket
      ? formatDate(appointmentTicket.value, "d")
      : "";

    const month = appointmentTicket
      ? formatDate(appointmentTicket.value, "LLLL")
      : "";

    const year = appointmentTicket
      ? formatDate(appointmentTicket.value, "kkkk")
      : "";

    const dateOfYear = `${weekDay.toLowerCase()}, ${day} ${month.toLowerCase()}`;

    const doctorSpeciality: string =
      store.specialistStore.currentSpecialistSpeciality;

    const fullNameOfPatient = patient
      ? `${patient.lname} ${patient.fname} ${patient.mname}`
      : "";

    const currentTimeSlot = appointmentTicketTime
      ? appointmentTicketTime.value.slice(0, -3)
      : "";

    const hospitalName = hospital ? hospital.organisationName : "";

    const hospitalAddress = hospital ? hospital.address : "";

    const doctorName = specialist ? specialist.lname : "";

    useEffect(() => {
      setOpen(isOpened);
    }, [isOpened]);

    const confirm = () => {
      if (
        time &&
        currentCity?.area_code &&
        hospital?.idHospital &&
        patient?.idUserData &&
        specialist?.idDoctor &&
        speciality?.idSpecialty &&
        appointmentTicketTime?.idTicket
      ) {
        const date = new Date(time);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        const payload: IAppointmentCreatePayload = {
          id_region: currentCity.area_code,
          id_user_data: patient.idUserData,
          id_doctor: specialist.idDoctor,
          id_hospital: hospital.idHospital,
          id_specialty: speciality.idSpecialty,
          id_ticket_external: appointmentTicketTime.idTicket,
          dt: `${day.toString().length === 2 ? day : `0${day}`}.${
            month.toString().length === 2 ? month : `0${month}`
          }.${year} ${currentTimeSlot}`,
        };
        onConfirm(payload);
      }
    };

    return (
      <BaseModal isOpened={open} onClose={onClose} onConfirm={confirm}>
        <div className={s.title}>
          <p className="headline-2">Запись к врачу</p>
        </div>
        <div className={s.userData}>
          <div className={s.dataBlock}>
            <p className={s.label}>Пациент</p>
            <p className={s.text}>{fullNameOfPatient}</p>
          </div>
          <div className={s.dataBlock}>
            <p className={s.label}>Дата</p>
            <p className={s.text}>{dateOfYear + " " + year}</p>
          </div>
          <div className={s.dataBlock}>
            <p className={s.label}>Время приема</p>
            <p className={s.text}>{currentTimeSlot}</p>
          </div>
          <div className={s.dataBlock}>
            <p className={s.label}>Больница</p>
            <p className={s.text}>{hospitalName}</p>
            <p className={s.help}>{hospitalAddress}</p>
          </div>
          <div className={s.dataBlock}>
            <p className={s.label}>Врач {doctorSpeciality}</p>
            <p className={s.text}>{doctorName}</p>
          </div>
        </div>
      </BaseModal>
    );
  }
);

AppointmentConfirmModal.displayName = "AppointmentConfirmModal";

export default AppointmentConfirmModal;
